import {action, makeAutoObservable, observable} from 'mobx';
import TableInterface from 'app/interfaces/poker/TableInterface';
import PlayerInterface from 'app/interfaces/poker/PlayerInterface';
import musicPlayer from 'app/components/sounds';
import {ONE_SECOND_MS, HALF_OF_SECOND_MS, THIRD_INDEX, FOURTH_INDEX, FIFTH_INDEX} from 'app/store/constants/storeConstants';
import {
    subscribePokerTable,
    unsubscribe
} from 'modules/WsSubscription';
import {pokerStore} from 'app/store/global';
import {PokerController} from 'app/controllers/PokerController';

export const STATE_WAIT = 'wait';
export const STATE_SIT_IN = 'sit_in';
export const STATE_BUY_IN = 'buy_in';

const SHOWDOWN_CASES = {
    FLOP: [THIRD_INDEX, FOURTH_INDEX],
    PRE_FLOP: [0, THIRD_INDEX],
    TURN: [FOURTH_INDEX, FIFTH_INDEX]
};

const SHOWDOWN_STATES_TO_REVEAL = [
    'PRE_FLOP',
    'FLOP',
    'TURN'
];

interface ActionParams {
    action: string
    position: number
}

export interface StateParamsInterface {
    position?: number
}

export interface WinnerInterface {
    winner_index: number
    best_hand: string[]
}

export class TableModel {
    @observable currentTable: TableInterface = null;

    @observable player: PlayerInterface = null;

    @observable winners: WinnerInterface[] = [];

    @observable preAction: string = null;

    @observable communityCards: string[] = [];

    @observable showdownState: string = null;

    @observable showdownProcessing: boolean = false;

    @observable isMinimize = false;

    @observable isEnabledSound = true;

    @observable isLoading = false;

    @observable subscription = null;

    @observable state = STATE_WAIT;

    @observable stateParams: StateParamsInterface = {};

    @observable lastActionParams: ActionParams = null;

    name: string = '';

    lastActionInterval = null;

    controller: PokerController = null;

    constructor(table: TableInterface) {
        this.currentTable = table;
        this.communityCards = table.cards;
        this.player = table.player;
        this.name = `NL ${table.big_blind * 100}`;
        this.setWinners(table);
        this.subcribeChannel();
        makeAutoObservable(this);
    }

    setController = (controller: PokerController): void => {
        this.controller = controller;
    };

    setShowdownState = (state: string, cards: string[]): void => {
        if (state !== null && this.showdownState !== state && SHOWDOWN_STATES_TO_REVEAL.includes(state)) {
            this.revealCommunityCards(state);
        } else {
            this.communityCards = cards;
        }

        this.showdownState = state;
    };

    @action
    updateLoadingState(state: boolean): void {
        this.isLoading = state;
    }

    @action
    closeTable(): void {
        this.unsubcribeChannel();
        pokerStore.closeTable(this.currentTable.table_id);
    }

    @action
    minimizeTable(): void {
        this.isMinimize = !this.isMinimize;
    }

    @action
    toggleSound = (): void => {
        this.isEnabledSound = !this.isEnabledSound;
    };

    @action
    updateCurrentTable = (table: TableInterface): void => {
        this.currentTable = table;
        this.setShowdownState(table.showdown_state, table.cards);
        this.setWinners(table);
    };

    @action
    setWinners = (table: TableInterface): void => {
        const winners = table.players?.filter(x => x.winner_index !== null).map(({best_hand, winner_index}) => ({best_hand, winner_index})) || [];

        if (winners.length > 0) {
            this.winners = winners;
        } else {
            this.winners = [];
        }
    };

    @action
    setPreAction = (action: string): void => {
        this.preAction = action;
    };

    @action
    usePreAction = (): void => {
        switch (this.preAction) {
        case 'check':
            if (this.isCheck()) {
                this.controller.check(this.currentTable.table_id);
            }
            break;
        case 'check/fold':
            if (this.isCheck()) {
                this.controller.check(this.currentTable.table_id);
            } else {
                this.controller.fold(this.currentTable.table_id);
            }
            break;
        case 'call':
            if (!this.isCheck()) {
                this.controller.call(this.currentTable.table_id);
            }
            break;
        case 'fold':
            this.controller.fold(this.currentTable.table_id);
            break;
        default: break;
        }

        this.preAction = '';
    };

    isCheck = (): boolean => {
        const maxBet = Math.max(...this.currentTable.players?.map(item => item.bet) || [0]);

        return this.player.bet >= maxBet;
    };

    @action
    updatePlayer(player: PlayerInterface): void {
        this.player = player;
    }

    @action
    unsubcribeChannel = (): void => {
        if (this.subscription) {
            this.subscription = unsubscribe(this.subscription);
        }
    };

    @action
    subcribeChannel = (): void => {
        this.subscription = subscribePokerTable(this.currentTable.table_id, ({data: {event, payload}}) => {
            this.updateCurrentTable(payload.table);
            this.setLastActionParams({action: event, position: payload.event_params.position});
            this.setLastActionInterval();
            musicPlayer.play(`poker_${event === 'bet' ? 'raise' : event}_sound`, this.isEnabledSound);

            if (this.player) {
                if (payload.table.action_index !== this.player.position && this.preAction === 'check/fold' && !this.isCheck()) {
                    this.preAction = 'fold';
                }

                if (payload.table.finished || event === 'raise' && this.preAction === 'call') {
                    this.preAction = '';
                }

                if (payload.table.action_index === this.player.position &&
                    this.preAction &&
                    !payload.table.finished) {
                    this.usePreAction();
                }
            }
        });
    };

    setLastActionInterval = (): void => {
        if (this.lastActionInterval) {
            clearTimeout(this.lastActionInterval);
        }

        if (this.lastActionParams?.position !== null) {
            this.lastActionInterval = setTimeout(() => {
                this.setLastActionParams(null);
            }, ONE_SECOND_MS);
        }
    };

    @action
    sitIn = (position: number): void => {
        this.stateParams = {position};
        this.state = STATE_SIT_IN;
    };

    @action
    buyIn = (): void => {
        this.state = STATE_BUY_IN;
    };

    @action
    clearState = (): void => {
        this.stateParams = {};
        this.state = STATE_WAIT;
    };

    @action
    setLastActionParams = (params: ActionParams): void => {
        this.lastActionParams = params;
    };

    isWaiting = (): boolean => this.state === STATE_WAIT;

    isSitIn = (): boolean => this.state === STATE_SIT_IN;

    isBuyIn = (): boolean => this.state === STATE_BUY_IN;

    @action
    setCommunityCards = (from: number, to: number): void => {
        this.communityCards = [...this.communityCards, ...this.currentTable.cards.slice(from, to)];
    };

    revealCommunityCards(showdownState: string): void {
        this.showdownProcessing = true;

        const statesToReveal = SHOWDOWN_STATES_TO_REVEAL.slice(SHOWDOWN_STATES_TO_REVEAL.findIndex(x => x === showdownState), THIRD_INDEX);

        statesToReveal.forEach((state, index) => {
            setTimeout(() => {
                const indexses = SHOWDOWN_CASES[state];
                this.setCommunityCards(indexses[0], indexses[1]);

                if (index === statesToReveal.length - 1) {
                    this.showdownProcessing = false;

                    if (this.player?.winner_index) {
                        musicPlayer.play('poker_win_sound', this.isEnabledSound);
                    }
                }
            }, index * HALF_OF_SECOND_MS);
        });
    }
}
